import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Alert,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";
import { HeroSection } from "Components/Shared/HeroSection";
import { CampaignSignupOptions } from "Components/Transactions/Anonymous/SignUpOptions/CampaignSignupOptions";
import { ModalDialog } from "Components/Shared/Dialogs/ModalDialog";
import { getCampaignImages } from "Utils/Campaigns/campaignImageMapping";
import { getCampaignImageUrl } from "Api/Queries/Campaigns/useCampaignImageUrl";
import { CampaignImageType } from "Infrastructure/Api/Api";
import { CampaignTimeline } from "Components/Campaigns/CampaignTimeline";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useCampaignByCodeQuery } from "Api/Queries/Campaigns/useCampaignByCodeQuery";
import { darkenColor } from "Utils/ColorUtils";

type Props = {
  campaignCode: string;
};

export const SuccessfulTransactionCampaignInfo = ({ campaignCode }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const theme = useTheme();
  const { t } = useResource();

  const {
    data: campaignData,
    isLoading: isCampaignDataLoading,
    isError: isCampaignDataError,
  } = useCampaignByCodeQuery(campaignCode);

  if (isCampaignDataLoading) {
    return <CircularProgress />;
  }

  if (isCampaignDataError || !campaignData) {
    return (
      <Alert severity="error">
        {t(Resources.Campaigns.Campaign.Page.CampaignNotFoundMessage)}
      </Alert>
    );
  }

  const campaignImages = getCampaignImages(campaignCode);
  const apiThankYouUrl = getCampaignImageUrl(
    campaignData,
    CampaignImageType.ThankYou,
  );
  const thankYouImage = apiThankYouUrl || campaignImages?.thankYou;

  const apiIconUrl = getCampaignImageUrl(campaignData, CampaignImageType.Icon);

  const fallbackColor = theme.palette.primary.main;
  const iconColor = campaignData.color ?? fallbackColor;
  const iconHoverColor = darkenColor(iconColor, 15);

  return (
    <Stack mx={[-2.5, -6.25]} mt={[-2.5, -6.25]} gap={3} pb={10}>
      {/* negative margins as a walk-around to override the parent's Paper's padding and stretch the image*/}
      <HeroSection
        backgroundImage={thankYouImage}
        {...(apiIconUrl
          ? { iconUrl: apiIconUrl }
          : {
              icon: (
                <FavoriteIcon sx={{ fontSize: "42px", color: iconColor }} />
              ),
            })}
      />
      <Typography variant="h4" component="h1" textAlign="center" px={4}>
        {t(Resources.Common.ThankYou)}!
      </Typography>
      <Divider
        sx={{ opacity: 0.5, width: "85%", mx: "auto", mt: [3, 5], mb: [2, 3] }}
      />
      <Typography
        px={4}
        py={2}
        fontWeight={600}
        fontSize="1.125rem"
        textAlign="center"
      >
        {campaignData?.successfulPaymentText}
      </Typography>
      <Box mx={10}>
        <CampaignTimeline
          campaignName={campaignData?.name || ""}
          paymentInstructions={campaignData?.paymentInstructionsText}
          onlyWithMoone={campaignData?.onlyWithMooneText}
          inactiveColor={iconColor}
        />
      </Box>
      <ModalDialog
        title=""
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <CampaignSignupOptions
          Icon={FavoriteIcon}
          campaignName={campaignData?.name || ""}
          iconColor={iconColor}
        />
      </ModalDialog>
      <Paper
        sx={{
          boxShadow: "none",
          position: "fixed",
          borderRadius: 0,
          zIndex: 2,
          bottom: 0,
          pt: 11,
          pb: 4,
          px: { xs: 3, sm: 5 },
          mx: { xs: 0, sm: 0 },
          width: { xs: "calc(100% - 24px)", sm: "600px" },
          background: `linear-gradient(180deg, transparent 0%, ${theme.palette.background.paper} 30%)`,
        }}
      >
        <Button
          fullWidth
          color="primary"
          variant="contained"
          onClick={() => setIsModalOpen(true)}
          sx={{
            py: 1.5,
            fontWeight: 600,
            borderRadius: "10px",
            textTransform: "uppercase",
            backgroundColor: iconColor,
            "&:hover": {
              backgroundColor: iconHoverColor,
            },
            "&:active": {
              backgroundColor: iconColor,
            },
            "&:focus": {
              backgroundColor: iconColor,
            },
          }}
        >
          {t(Resources.Campaigns.Campaign.CreateAccountButton)}
        </Button>
      </Paper>
    </Stack>
  );
};
